import Phaser from "phaser";
import {
  DUCK_DROP_MS,
  DUCK_RISE_MS,
  FAST_FALL_MUL,
  GAME_OVER_HIT_MS,
  HURT_STUN_MS,
  JUMP_CLEAR_END,
  JUMP_CLEAR_START,
  JUMP_MS,
  LANE_CHANGE_MS,
  type Lane,
} from "../constants";
import { applySpriteOrigin } from "../assets/loader";
import { addShadowBlob, sizeBlob, TEX_PIXEL } from "../assets/primitives";
import { advanceDuckPhase, duckClearsBranch, type DuckPhase } from "../logic/duck";
import { canStartFastFall, jumpActionDeltaMs, jumpProgress } from "../logic/jumpFastFall";
import type { ProjectionContext } from "../systems/RunnerProjection";
import { audioManager } from "../systems/AudioManager";
import { motionFx, type MotionFxConfig } from "../systems/motionFx";
import { setDepthIfChanged } from "../setDepthIfChanged";
import { resetFxSprite, type FxSpritePool } from "../systems/fxPool";

export type BearMotion =
  | "running"
  | "laneChanging"
  | "jumping"
  | "ducking"
  | "hurt"
  | "finishing"
  | "gameOver";

export class Bear {
  readonly sprite: Phaser.GameObjects.Sprite;
  readonly shadow: Phaser.GameObjects.Image;
  lane: Lane = 0;
  motion: BearMotion = "running";
  landingKick = 0;
  private visualLaneValue = 0;
  private laneFrom = 0;
  private actionMs = 0;
  private actionDuration = 0;
  private dustTimer = 0;
  private flashMs = 0;
  private squash = 1;
  private pendingGameOver = false;
  private fastFalling = false;
  private fastFallCue = 0;
  private duckPhase: DuckPhase = "holding";
  private readonly scene: Phaser.Scene;

  constructor(
    scene: Phaser.Scene,
    private readonly projection: ProjectionContext,
    private readonly fxSprites: FxSpritePool,
  ) {
    this.scene = scene;
    this.shadow = addShadowBlob(scene, 0, 0, 102, 28, 0.4);
    this.sprite = scene.add.sprite(0, 0, "bear-run-rear", 0);
    this.sprite.play("bear-run-rear");
    applySpriteOrigin(this.sprite);
    this.syncPose(0);
  }

  private get fx(): MotionFxConfig {
    return motionFx();
  }

  get visualLane(): number {
    return this.visualLaneValue;
  }

  get jumping(): boolean {
    return this.motion === "jumping";
  }

  get ducking(): boolean {
    return this.motion === "ducking" && duckClearsBranch(this.duckPhase, this.actionMs);
  }

  get airborne(): boolean {
    if (this.motion !== "jumping") {
      return false;
    }
    const t = this.actionMs / this.actionDuration;
    return t > JUMP_CLEAR_START && t < JUMP_CLEAR_END;
  }

  get canSteer(): boolean {
    return this.motion !== "finishing" && this.motion !== "gameOver" && !this.pendingGameOver;
  }

  tryLaneChange(next: Lane): boolean {
    if (!this.canSteer || next === this.lane) {
      return false;
    }
    if (this.motion === "hurt") {
      return false;
    }
    this.laneFrom = this.visualLaneValue;
    this.lane = next;
    if (this.motion === "jumping" || this.motion === "ducking") {
      audioManager.playSfx("whoosh");
      return true;
    }
    this.motion = "laneChanging";
    this.actionMs = 0;
    this.actionDuration = LANE_CHANGE_MS;
    this.sprite.play(next < this.laneFrom ? "bear-lane-left" : "bear-lane-right");
    audioManager.playSfx("whoosh");
    return true;
  }

  tryJump(): boolean {
    if (!this.canSteer || this.motion === "jumping" || this.motion === "ducking" || this.motion === "hurt") {
      return false;
    }
    this.motion = "jumping";
    this.fastFalling = false;
    this.fastFallCue = 0;
    this.actionMs = 0;
    this.actionDuration = JUMP_MS;
    this.sprite.anims.timeScale = 1;
    this.sprite.play("bear-jump-rear");
    audioManager.playSfx("jump");
    this.spawnDust("jump-takeoff-dust");
    return true;
  }

  tryDuck(): boolean {
    if (!this.canSteer || this.motion === "hurt") {
      return false;
    }
    if (this.motion === "jumping") {
      return this.tryFastFall();
    }
    if (this.motion === "ducking") {
      return false;
    }
    this.motion = "ducking";
    this.duckPhase = "dropping";
    this.actionMs = 0;
    this.actionDuration = DUCK_DROP_MS;
    this.playDuckDrop();
    audioManager.playSfx("duck");
    return true;
  }

  private tryFastFall(): boolean {
    const t = jumpProgress(this.actionMs, this.actionDuration);
    if (!canStartFastFall(true, this.fastFalling, t)) {
      return false;
    }
    this.fastFalling = true;
    this.fastFallCue = 1;
    this.spawnFastFallCue();
    audioManager.playSfx("whoosh");
    return true;
  }

  private advanceDuck(_deltaMs: number): void {
    const previous = this.duckPhase;
    const next = advanceDuckPhase(this.duckPhase, this.actionMs);
    if (next === "done") {
      this.motion = "running";
      this.sprite.anims.timeScale = 1;
      this.sprite.play("bear-run-rear", true);
      return;
    }
    if (next.phase === previous) {
      return;
    }
    this.duckPhase = next.phase;
    this.actionMs = next.phaseMs;
    if (next.phase === "holding") {
      this.holdDuckPose();
    } else if (next.phase === "rising") {
      this.playDuckRise();
    }
  }

  private playDuckDrop(): void {
    this.sprite.anims.timeScale = 1;
    this.sprite.play("bear-duck-rear");
    this.sprite.anims.timeScale = 200 / DUCK_DROP_MS;
  }

  private holdDuckPose(): void {
    if (this.sprite.anims?.isPlaying) {
      this.sprite.anims.pause();
    }
    this.sprite.setFrame(2);
    applySpriteOrigin(this.sprite);
  }

  private playDuckRise(): void {
    this.sprite.anims.timeScale = 1;
    this.sprite.play({ key: "bear-duck-rear", startFrame: 2, frameRate: 10 }, true);
    this.sprite.anims.timeScale = 200 / DUCK_RISE_MS;
  }

  hit(): void {
    this.motion = "hurt";
    this.fastFalling = false;
    this.fastFallCue = 0;
    this.actionMs = 0;
    this.actionDuration = HURT_STUN_MS;
    this.flashMs = 1500;
    this.sprite.anims.timeScale = 1;
    this.sprite.play("bear-hit-rear");
    audioManager.playSfx("hit");
    this.spawnDust("wolf-hit-stars");
  }

  startFinish(): void {
    this.motion = "finishing";
    this.sprite.play("bear-finish-rear");
  }

  startGameOver(): void {
    this.pendingGameOver = false;
    this.motion = "gameOver";
    this.actionMs = 0;
    this.sprite.play("bear-game-over");
  }

  /** Keep the hit clip on-screen, then swap to the game-over pose. */
  beginGameOver(): void {
    this.pendingGameOver = true;
    if (this.motion !== "hurt") {
      this.hit();
    }
    this.actionDuration = GAME_OVER_HIT_MS;
  }

  update(deltaMs: number, _invulnerable: boolean): void {
    const jumpT = this.motion === "jumping" ? jumpProgress(this.actionMs, this.actionDuration) : 0;
    this.actionMs += jumpActionDeltaMs(deltaMs, jumpT, this.fastFalling, FAST_FALL_MUL);
    if (this.sprite.anims) {
      if (this.fastFalling && jumpT >= 0.5) {
        this.sprite.anims.timeScale = FAST_FALL_MUL;
      } else if (this.motion !== "ducking") {
        this.sprite.anims.timeScale = 1;
      }
    }
    this.dustTimer += deltaMs;
    this.landingKick += (0 - this.landingKick) * Math.min(1, deltaMs / 90);
    this.squash += (1 - this.squash) * Math.min(1, deltaMs / 70);
    this.fastFallCue += (0 - this.fastFallCue) * Math.min(1, deltaMs / 80);

    if (this.motion === "laneChanging") {
      const t = Math.min(1, this.actionMs / this.actionDuration);
      this.visualLaneValue = this.laneFrom + (this.lane - this.laneFrom) * easeOut(t);
      if (t >= 1) {
        this.visualLaneValue = this.lane;
        this.motion = "running";
        this.sprite.play("bear-run-rear", true);
      }
    } else if (this.motion === "jumping") {
      this.visualLaneValue += (this.lane - this.visualLaneValue) * Math.min(1, deltaMs / 90);
      if (this.actionMs >= this.actionDuration) {
        this.motion = "running";
        this.fastFalling = false;
        this.fastFallCue = 0;
        this.sprite.anims.timeScale = 1;
        this.sprite.play("bear-run-rear", true);
        audioManager.playSfx("land");
        this.squash = this.fx.landingSquash;
        this.landingKick = 1;
        this.spawnDust("landing-dust");
        this.spawnLeafBurst();
      }
    } else if (this.motion === "ducking") {
      this.visualLaneValue += (this.lane - this.visualLaneValue) * Math.min(1, deltaMs / 90);
      this.advanceDuck(deltaMs);
    } else if (this.motion === "hurt") {
      this.visualLaneValue += (this.lane - this.visualLaneValue) * Math.min(1, deltaMs / 90);
      if (this.actionMs >= this.actionDuration) {
        if (this.pendingGameOver) {
          this.startGameOver();
        } else {
          this.motion = "running";
          this.sprite.play("bear-run-rear", true);
        }
      }
    } else if (this.motion === "running") {
      this.visualLaneValue += (this.lane - this.visualLaneValue) * Math.min(1, deltaMs / 90);
      if (this.dustTimer > 260) {
        this.dustTimer = 0;
        this.spawnDust("running-dust-puffs");
        this.spawnLeafBurst(0.45);
        audioManager.playSfx("footstep");
      }
    }

    if (this.flashMs > 0) {
      this.flashMs -= deltaMs;
      this.sprite.setAlpha(Math.sin(this.flashMs / 50) > 0 ? 1 : 0.35);
      if (this.flashMs <= 0) {
        this.sprite.setAlpha(1);
      }
    }

    this.syncPose(this.jumpLift());
  }

  applyPose(): void {
    this.syncPose(this.jumpLift());
  }

  private jumpLift(): number {
    if (this.motion !== "jumping") {
      return 0;
    }
    const t = this.actionMs / this.actionDuration;
    return Math.sin(Math.min(1, t) * Math.PI) * 118;
  }

  private syncPose(lift: number): void {
    const pose = this.projection.project(this.projection.frame.playerDistance, this.visualLaneValue);
    const air = lift / 118;
    const land = this.landingKick;
    this.shadow.setPosition(pose.x, pose.y + 6);
    const shadowW = (1.52 - air * 0.82) * (1 + land * 0.4);
    const shadowH = (0.8 - air * 0.52) * (1 + land * 0.28);
    sizeBlob(this.shadow, 102 * pose.scale * shadowW, 28 * pose.scale * shadowH);
    const crouched = this.motion === "ducking";
    const shadowAlpha = crouched ? 0.14 : 0.16 + (1 - air) * 0.3 + land * 0.22;
    this.shadow.setAlpha(shadowAlpha);
    setDepthIfChanged(this.shadow, pose.depth + 12);
    applySpriteOrigin(this.sprite);
    this.sprite.setPosition(pose.x, pose.y - lift);
    const bearScale = pose.scale * 1.22;
    const dive = this.fastFalling ? 0.9 - this.fastFallCue * 0.12 : 1;
    const jumpLocked = this.motion === "jumping" ? dive : this.squash;
    const widen = this.fastFalling ? 1 + this.fastFallCue * 0.1 : crouched ? 0.97 : 1;
    this.sprite.setScale(bearScale * widen, bearScale * jumpLocked);
    setDepthIfChanged(this.sprite, pose.depth + 24);
    const steer = this.lane - this.visualLaneValue;
    this.sprite.setRotation(steer * -0.07 + (this.motion === "laneChanging" ? steer * -0.04 : 0));
  }

  private spawnFastFallCue(): void {
    if (!this.fxSprites.alive || !this.scene.textures.exists(TEX_PIXEL)) {
      return;
    }
    const offsets = [-16, -2, 14];
    for (const [index, dx] of offsets.entries()) {
      const streak = this.fxSprites.acquire();
      resetFxSprite(streak, TEX_PIXEL);
      streak.setPosition(this.sprite.x + dx, this.sprite.y - 8 - index * 4);
      streak.setDisplaySize(3 + index, 14 + index * 4);
      streak.setTint(index === 1 ? 0xe8b84a : 0xf3e6c8);
      streak.setAlpha(0.8);
      streak.setDepth(this.sprite.depth + 2);
      this.scene.tweens.add({
        targets: streak,
        y: streak.y - 22,
        alpha: 0,
        duration: 150 + index * 20,
        onComplete: () => this.fxSprites.release(streak),
      });
    }
  }

  private spawnDust(key: string): void {
    if (!this.fxSprites.alive || !this.fx.runDust || !this.scene.anims.exists(key)) {
      return;
    }
    const dust = this.fxSprites.acquire();
    resetFxSprite(dust, key);
    dust.setPosition(this.shadow.x, this.shadow.y - 6);
    dust.setDepth(this.sprite.depth - 1);
    dust.setScale(0.38);
    dust.play(key);
    this.scene.tweens.add({
      targets: dust,
      y: dust.y + 36,
      scale: 0.62,
      alpha: 0.15,
      duration: 280,
      onComplete: () => this.fxSprites.release(dust),
    });
    dust.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => this.fxSprites.release(dust));
  }

  private spawnLeafBurst(chance = 1): void {
    if (!this.fxSprites.alive || !this.fx.leafBurst || Math.random() > chance) {
      return;
    }
    const key = this.scene.textures.exists("fallen-leaves-1") ? "fallen-leaves-1" : "";
    if (!key) {
      return;
    }
    const leaf = this.fxSprites.acquire();
    resetFxSprite(leaf, key);
    leaf.setPosition(this.shadow.x + (Math.random() * 18 - 9), this.shadow.y);
    leaf.setDepth(this.sprite.depth - 2).setScale(0.18).setAlpha(0.8);
    this.scene.tweens.add({
      targets: leaf,
      x: leaf.x + (Math.random() * 40 - 20),
      y: leaf.y + 28,
      scale: 0.32,
      alpha: 0,
      angle: Math.random() * 50 - 25,
      duration: 420,
      onComplete: () => this.fxSprites.release(leaf),
    });
  }
}

function easeOut(t: number): number {
  return 1 - (1 - t) * (1 - t);
}
